import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from "@angular/router";
import { ApplicationType, ENV, LoggerService } from "@bequeathed/ngx-app-core";
import { AnalyticsEventName, InterviewService } from "@bequeathed/ngx-services";
import { BaseForm } from '../../../core/ui/base-form/base-form.component';
import { AnalyticsUtils } from '../../../core/utils/analytics.utils';

const LOG: LoggerService = new LoggerService('CompleteComponent');

/**
 * Simply does a redirect to the home app after confirming interview completion with the back-end. This step triggers an event
 * in Intercom and uploads the users documents to SharePoint.
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'app-interview-complete',
    templateUrl: './complete.component.html',
    styleUrls: ['./complete.component.sass'],
    standalone: true,
    encapsulation: ViewEncapsulation.None
})
export class CompleteComponent extends BaseForm implements OnInit {

    interviewId: number | undefined;

    private title = inject(Title);
    protected interviewService = inject(InterviewService);
    private route = inject(ActivatedRoute);

    constructor() {
        super();
        this.title.setTitle('Will Interview : Complete');
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.route.params.subscribe((params: any) => {
            this.type = params['type'];
            this.interviewId = params['id'];

            this.listOfWills().then(() => LOG.debug('init', 'Redirecting to my wishes...'));
        });
    }

    async listOfWills(): Promise<void> {

        // FixMe :: Check if is-complete first (check sharepoint locations)
        await this.interviewService.completeProcess(this.type!, this.interviewId!);

        // Funnel stage: interview_process_complete. The whole will-writing process
        // is confirmed complete with the back-end here, before redirecting the user
        // back to the home app (design §5).
        this.analytics.trackFunnelStage(AnalyticsEventName.INTERVIEW_PROCESS_COMPLETE, {
            customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
            page: `/interview/WILL/${this.interviewState()?.interviewId ?? 'unknown'}/complete`,
            interview_id: this.interviewState()?.interviewId ?? ''
        });

        const homeApp = ENV.applications.get(ApplicationType.HOME)!
        this.redirectToDashboard(`${homeApp.baseUrl}${homeApp.api.get('dashboard')}`);
    }

    /**
     * Redirects the browser to the given home-app dashboard URL. Extracted so the
     * surrounding completion flow (back-end confirmation + the `interview_process_complete`
     * funnel event) can be unit-tested without the test triggering a real page
     * navigation — mirrors the `goToHome`/`goToWills` redirect helpers used by the
     * sibling chat-interface component.
     *
     * @param url the fully-resolved dashboard URL to navigate to
     */
    protected redirectToDashboard(url: string): void {
        window.location.href = url;
    }
}
